import { create } from 'zustand';
import api from '../utils/api';
import { useActivityLogStore } from './activityLogStore';
import { autoAddSingleBxgyY, idTail } from '../utils/bxgyY';

// Cart line holding units the single-Y auto-add put in for a BXGY code (rec = { variantId, locationId, customSize }).
const autoLineIndex = (items, rec) => (items || []).findIndex(it => {
  if (idTail(it.variantId) !== rec.variantId || String(it.locationId == null ? '' : it.locationId) !== rec.locationId) return false;
  const size = ((it.customAttributes || []).find(a => a.key === '_custom_size') || {}).value || null;
  return size === rec.customSize;
});
// Keep the auto-added records in step with the cart: units the staff removed by hand are no longer
// "ours" to remove later.
const pruneAutoAdded = (auto, items) => {
  const next = {};
  Object.entries(auto || {}).forEach(([code, recs]) => {
    const kept = recs.map(r => {
      const i = autoLineIndex(items, r);
      return i < 0 ? null : { ...r, qty: Math.min(r.qty, parseInt(items[i].quantity, 10) || 0) };
    }).filter(r => r && r.qty > 0);
    if (kept.length) next[code] = kept;
  });
  return next;
};

export const useCartStore = create((set, get) => ({
  cart: {
    items: [],
    subtotal: 0,
    discounts: [],
    giftCards: [],
    total: 0
  },
  isLoading: false,
  // BXGY codes the staff removed by hand — never auto-applied again until the cart is cleared/emptied.
  bxgyDismissed: [],
  dismissBxgy: (code) => set(s => ({ bxgyDismissed: s.bxgyDismissed.includes(code) ? s.bxgyDismissed : [...s.bxgyDismissed, code] })),
  resetBxgyDismissed: () => set({ bxgyDismissed: [] }),
  // BXGY code → Y units added automatically when the code was applied (offer with a single Y variant).
  // Removing the code takes exactly those units back out of the cart.
  bxgyAutoAdded: {},
  recordBxgyAutoAdded: (code, added) => set(s => {
    if (!added || !added.length) return {};
    const merged = (s.bxgyAutoAdded[code] || []).map(m => ({ ...m }));
    added.forEach(a => {
      const same = merged.find(m => m.variantId === a.variantId && m.locationId === a.locationId && m.customSize === a.customSize);
      if (same) same.qty += a.qty; else merged.push({ variantId: a.variantId, locationId: a.locationId, customSize: a.customSize, qty: a.qty });
    });
    return { bxgyAutoAdded: { ...s.bxgyAutoAdded, [code]: merged } };
  }),

  fetchCart: async () => {
    try {
      const response = await api.get('/cart');
      set({ cart: response.data.cart });
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  },

  addToCart: async (item) => {
    set({ isLoading: true });
    try {
      const response = await api.post('/cart/add', item);
      set({ cart: response.data.cart, isLoading: false });
      useActivityLogStore.getState().logProductAdded(item);
      return { success: true };
    } catch (error) {
      console.error('Error adding to cart:', error);
      set({ isLoading: false });
      return { success: false, error: error.response?.data?.error };
    }
  },

  updateQuantity: async (variantId, quantity, itemIndex) => {
    set({ isLoading: true });
    try {
      const oldItem = get().cart.items[itemIndex];
      const oldQty = oldItem?.quantity || 0;
      // Send the cart snapshot so the server can rebuild it if its in-memory copy was lost to a restart.
      const response = await api.put('/cart/update', { variantId, quantity, itemIndex, cart: get().cart });
      set(s => ({ cart: response.data.cart, isLoading: false, bxgyAutoAdded: pruneAutoAdded(s.bxgyAutoAdded, response.data.cart.items) }));
      if (oldItem) useActivityLogStore.getState().logQuantityUpdated(oldItem, oldQty, quantity);
      return { success: true };
    } catch (error) {
      console.error('Error updating cart:', error);
      set({ isLoading: false });
      return { success: false, error: error.response?.data?.error };
    }
  },

  removeFromCart: async (variantId, itemIndex) => {
    set({ isLoading: true });
    try {
      const removedItem = get().cart.items[itemIndex];
      // Send the cart snapshot so the server can rebuild it if its in-memory copy was lost to a restart.
      const response = await api.post('/cart/remove', { variantId, itemIndex, cart: get().cart });
      set(s => ({ cart: response.data.cart, isLoading: false, bxgyAutoAdded: pruneAutoAdded(s.bxgyAutoAdded, response.data.cart.items) }));
      if (removedItem) useActivityLogStore.getState().logProductRemoved(removedItem);
      return { success: true };
    } catch (error) {
      console.error('Error removing from cart:', error);
      set({ isLoading: false });
      return { success: false, error: error.response?.data?.error };
    }
  },

  // opts.autoAddY (default true): a BXGY code whose Y list is ONE variant adds that variant for the
  // missing units right after it is applied (staff applied the code by hand — typed or picked it).
  // The background auto-apply and the "Add offer item" picker pass false (they handle Y themselves).
  applyDiscount: async (discountCode, locationId, opts = {}) => {
    set({ isLoading: true });
    try {
      const response = await api.post('/cart/apply-discount', { discountCode, locationId });
      set({ cart: response.data.cart, isLoading: false });
      useActivityLogStore.getState().logDiscountApplied(discountCode);
      const applied = (response.data.cart.discounts || []).find(d => d.level === 'bxgy' && String(d.code).toUpperCase() === String(discountCode).trim().toUpperCase());
      if (opts.autoAddY !== false && applied && applied.bxgyNeedQty > 0) {
        const r = await autoAddSingleBxgyY({ code: applied.code, needQty: applied.bxgyNeedQty, locationId, addToCart: get().addToCart });
        get().recordBxgyAutoAdded(applied.code, r.added);
        const addedText = r.added.map(a => `${a.qty} × ${a.title}`).join(', ');
        const message = [addedText ? `${applied.code} applied — added ${addedText} to the cart` : '', r.note || ''].filter(Boolean).join('. ');
        return { success: true, message: message || response.data.message, autoAdded: r.added };
      }
      return { success: true, message: response.data.message };
    } catch (error) {
      console.error('Error applying discount:', error);
      set({ isLoading: false });
      return { success: false, error: error.response?.data?.error };
    }
  },

  removeDiscount: async (discountCode) => {
    try {
      // Send the current cart snapshot so the server can rebuild it if its in-memory
      // copy was lost to a restart (otherwise removal would 404 with "cart not found").
      const wasBxgy = (get().cart.discounts || []).some(d => d.code === discountCode && d.level === 'bxgy');
      const response = await api.post('/cart/remove-discount', { discountCode, cart: get().cart });
      set({ cart: response.data.cart });
      if (wasBxgy) get().dismissBxgy(discountCode); // staff chose to remove it — don't auto-apply it back
      useActivityLogStore.getState().logDiscountRemoved(discountCode);
      // Take out the Y units this code added automatically (see applyDiscount) — only those units;
      // anything the staff added themselves stays.
      const recs = (get().bxgyAutoAdded || {})[discountCode] || [];
      set(s => { const n = { ...s.bxgyAutoAdded }; delete n[discountCode]; return { bxgyAutoAdded: n }; });
      for (const rec of recs) {
        const items = get().cart.items || [];
        const i = autoLineIndex(items, rec);
        if (i < 0) continue;
        const left = (parseInt(items[i].quantity, 10) || 0) - rec.qty;
        if (left > 0) await get().updateQuantity(items[i].variantId, left, i);
        else await get().removeFromCart(items[i].variantId, i);
      }
      return { success: true };
    } catch (error) {
      console.error('Error removing discount:', error);
      return { success: false, error: error.response?.data?.error };
    }
  },

  applyGiftCard: async (giftCardCode) => {
    set({ isLoading: true });
    try {
      const response = await api.post('/cart/apply-gift-card', { giftCardCode });
      set({ cart: response.data.cart, isLoading: false });
      useActivityLogStore.getState().logGiftCardApplied(giftCardCode, response.data.giftCard?.balance);
      return { success: true, giftCard: response.data.giftCard };
    } catch (error) {
      console.error('Error applying gift card:', error);
      set({ isLoading: false });
      return { success: false, error: error.response?.data?.error };
    }
  },

  removeGiftCard: async (giftCardCode) => {
    try {
      const response = await api.post('/cart/remove-gift-card', { giftCardCode });
      set({ cart: response.data.cart });
      useActivityLogStore.getState().logGiftCardRemoved(giftCardCode);
      return { success: true };
    } catch (error) {
      console.error('Error removing gift card:', error);
      return { success: false, error: error.response?.data?.error };
    }
  },

  clearCart: async () => {
    try {
      await api.post('/cart/clear');
      set({
        bxgyDismissed: [],
        bxgyAutoAdded: {},
        cart: {
          items: [],
          subtotal: 0,
          discounts: [],
          giftCards: [],
          total: 0
        }
      });
      useActivityLogStore.getState().logCartCleared();
      return { success: true };
    } catch (error) {
      console.error('Error clearing cart:', error);
      return { success: false, error: error.response?.data?.error };
    }
  }
}));
